import dayjs, { Dayjs } from 'dayjs';
import { Tone } from '../ui';

/** The `t` from useTranslation(), loosely typed so this file doesn't depend on i18next's generics */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/*
 * Where a project stands against its planned start / end dates. Shared by the Projects page,
 * the dashboard and the Tasks page, so the same project gets the same alert everywhere.
 */

/** How many days ahead a start or an end counts as "soon". */
export const SOON_DAYS = 7;

export type ScheduleState =
  | 'unscheduled' // no dates at all
  | 'upcoming' // starts later than SOON_DAYS from now
  | 'startsSoon' // starts today or within SOON_DAYS
  | 'running' // started, end not near (or no end date)
  | 'endsSoon' // ends today or within SOON_DAYS
  | 'overdue' // end date passed while the project is still active
  | 'closed'; // finished or canceled — never alerts

export interface Schedule {
  state: ScheduleState;
  start: Dayjs | null;
  end: Dayjs | null;
  /** Days until the start (0 = today); set while the project hasn't started */
  daysToStart?: number;
  /** Days until the end (0 = today, negative = past); set when there is an end date */
  daysToEnd?: number;
  /** 0–100 through the planned period, when both dates are set */
  progress?: number;
  /** Planned length in days, both ends included */
  totalDays?: number;
  /** Day number within the period (1 = start day), while it is running */
  currentDay?: number;
}

interface ScheduledProject {
  status: string;
  start_date?: string | null;
  end_date?: string | null;
}

const parse = (value?: string | null) => (value ? dayjs(value.slice(0, 10)) : null);

export function getSchedule(project: ScheduledProject, today: Dayjs = dayjs()): Schedule {
  const day = today.startOf('day');
  const start = parse(project.start_date);
  const end = parse(project.end_date);
  const schedule: Schedule = { state: 'unscheduled', start, end };

  if (start && end) {
    schedule.totalDays = end.diff(start, 'day') + 1;
    const elapsed = day.diff(start, 'day') + 1;
    schedule.progress = Math.max(0, Math.min(100, Math.round((elapsed / schedule.totalDays) * 100)));
    if (elapsed >= 1 && elapsed <= schedule.totalDays) schedule.currentDay = elapsed;
  }
  if (end) schedule.daysToEnd = end.diff(day, 'day');
  if (start && !start.isBefore(day)) schedule.daysToStart = start.diff(day, 'day');

  if (project.status !== 'active') schedule.state = 'closed';
  else if (!start && !end) schedule.state = 'unscheduled';
  else if (schedule.daysToStart !== undefined && schedule.daysToStart > 0)
    schedule.state = schedule.daysToStart <= SOON_DAYS ? 'startsSoon' : 'upcoming';
  // Starting today is worth an alert even when the end is near too
  else if (schedule.daysToStart === 0) schedule.state = 'startsSoon';
  else if (schedule.daysToEnd === undefined) schedule.state = 'running';
  else if (schedule.daysToEnd < 0) schedule.state = 'overdue';
  else if (schedule.daysToEnd <= SOON_DAYS) schedule.state = 'endsSoon';
  else schedule.state = 'running';

  return schedule;
}

export const isAlert = (schedule: Schedule) =>
  schedule.state === 'startsSoon' || schedule.state === 'endsSoon' || schedule.state === 'overdue';

export const SCHEDULE_TONE: Record<ScheduleState, Tone> = {
  unscheduled: 'neutral',
  upcoming: 'neutral',
  startsSoon: 'violet',
  running: 'success',
  endsSoon: 'warning',
  overdue: 'danger',
  closed: 'neutral',
};

/** Short status text, e.g. "Starts tomorrow", "Ends in 3 days", "4 days overdue". */
export function scheduleLabel(t: Translate, schedule: Schedule): string {
  const { state, daysToStart = 0, daysToEnd = 0 } = schedule;
  switch (state) {
    case 'upcoming':
    case 'startsSoon':
      if (daysToStart === 0) return t('projects.schedule.startsToday');
      if (daysToStart === 1) return t('projects.schedule.startsTomorrow');
      return t('projects.schedule.startsIn', { count: daysToStart });
    case 'endsSoon':
      if (daysToEnd === 0) return t('projects.schedule.endsToday');
      if (daysToEnd === 1) return t('projects.schedule.endsTomorrow');
      return t('projects.schedule.endsIn', { count: daysToEnd });
    case 'overdue':
      return t('projects.schedule.overdue', { count: -daysToEnd });
    case 'running':
      return schedule.end ? t('projects.schedule.daysLeft', { count: daysToEnd }) : t('projects.schedule.ongoing');
    case 'closed':
      return '';
    default:
      return t('projects.schedule.noDates');
  }
}

/** Alert rows: overdue first (longest overdue on top), then ending, then starting — soonest first. */
export function projectAlerts<P extends ScheduledProject>(projects: P[], today: Dayjs = dayjs()) {
  const rank = { overdue: 0, endsSoon: 1, startsSoon: 2 } as Record<ScheduleState, number>;
  const key = (s: Schedule) => (s.state === 'startsSoon' ? s.daysToStart ?? 0 : s.daysToEnd ?? 0);
  return projects
    .map((project) => ({ project, schedule: getSchedule(project, today) }))
    .filter((entry) => isAlert(entry.schedule))
    .sort((a, b) => rank[a.schedule.state] - rank[b.schedule.state] || key(a.schedule) - key(b.schedule));
}

/** "3 Oct 2026" in the UI language. */
export const formatScheduleDate = (value: Dayjs, language: string, withYear = true) =>
  value.toDate().toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  });
